import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { scaleBand, scaleLinear, scaleOrdinal } from 'd3-scale';
import { AnimatedOrderedList } from '../shared/horizontal-bar/animated-ordered-list/AnimatedOrderedList';
import { HorizontalBarWithPlaceholder } from '../shared/horizontal-bar/horizontal-bar-with-placeholder/HorizontalBarWithPlaceholder';
import { OptionLabel } from '../shared/horizontal-bar/option-label/OptionLabel';
import type { Visualization } from '../types';
import type { RankingBarsRenderChoiceVisualization } from '../utils/types/render-options-by-slide-type/ranking';
import type { RenderChoiceLabel } from '../utils/types/render-options-by-slide-type/types';

const MAX_OPTION_HEIGHT = 180;
const MAX_OPTIONS_GAP = 32;
const SCORE_WIDTH = 72;
const SCORE_PADDING_RIGHT = 16;
const MIN_SCALE_RESULT_WIDTH = 8;

export interface RankingData {
  id: string;
  label: string;
  score: number;
}

export interface RankingStaticOptions {
  id: string;
  label: string;
}

const clamp = (min: number, value: number, max: number) => {
  return Math.max(min, Math.min(max, value));
};

interface RankingProps
  extends Visualization<RankingData, RankingStaticOptions> {
  isScoreHidden: boolean;
  placeholderColor: string;
  renderChoiceVisualization?: RankingBarsRenderChoiceVisualization | undefined;
  renderChoiceLabel?: RenderChoiceLabel | undefined;
  getOrderedListStyle?:
    | ((props: {
        index: number;
        key: string;
        length: number;
      }) => React.CSSProperties)
    | undefined;
  choiceLabelsContainerClassName?: string | undefined;
}

export const Ranking = ({
  width,
  height,
  colors,
  data,
  staticOptions,
  disableAnimations,
  keyAccessor = (d: RankingData | RankingStaticOptions) => d.id,
  labelAccessor = (d: RankingStaticOptions) => d.label,
  valueAccessor = (d: RankingData) => d.score,
  placeholderColor,
  isScoreHidden,
  renderChoiceVisualization,
  renderChoiceLabel,
  getOrderedListStyle,
  choiceLabelsContainerClassName,
}: RankingProps) => {
  const rootHeight = Math.max(height, 0);
  const rootWidth = Math.max(width, 0);

  const barsWidth = Math.max(rootWidth - SCORE_WIDTH - SCORE_PADDING_RIGHT, 0);

  const maxBoundedBarsHeight =
    MAX_OPTION_HEIGHT * staticOptions.length +
    MAX_OPTIONS_GAP * (staticOptions.length - 1);
  const barsHeight = Math.min(rootHeight, maxBoundedBarsHeight);

  const maxScore =
    data.length > 0 ? Math.max(...data.map((d) => valueAccessor(d) ?? 0)) : 0;

  const discreteValuesBasedOnOrder = staticOptions.map((_, i) => i.toString());
  const discreteValuesBasedOnId = staticOptions.map(keyAccessor);

  const fillColors = scaleOrdinal(colors).domain(discreteValuesBasedOnId);

  const yScale = scaleBand()
    .domain(discreteValuesBasedOnOrder)
    .range([0, barsHeight])
    .paddingInner(0.19)
    .paddingOuter(0.000001);

  const domainMax = maxScore > 0 ? maxScore : 1;
  const xScale = scaleLinear()
    .domain([0, domainMax])
    .range([MIN_SCALE_RESULT_WIDTH, barsWidth]);

  const sortedData = [...data].sort(
    (a, b) => valueAccessor(b) - valueAccessor(a),
  );
  const sortedDataWithKeys = sortedData.map((d, i) => ({
    ...d,
    key: keyAccessor(d),
    y: yScale(i.toString()) || 0,
  }));

  if (rootWidth <= 0 || rootHeight <= 0) {
    return null;
  }

  return (
    <Box
      width={rootWidth}
      height={rootHeight}
      justifyContent="center"
      alignItems="center"
      data-testid="ranking-visualization"
    >
      <Box width={rootWidth} height={barsHeight}>
        <AnimatedOrderedList
          disableAnimations={disableAnimations}
          rows={sortedDataWithKeys}
          itemHeight={yScale.bandwidth()}
          getOrderedListStyle={getOrderedListStyle}
          renderItem={(option, optionIndex) => {
            const optionResult = data.find(
              (d) => keyAccessor(d) === keyAccessor(option),
            );
            const id = keyAccessor(option);
            const score = optionResult ? valueAccessor(optionResult) : 0;
            const x = xScale(score);
            const color = fillColors(id);

            const height = yScale.bandwidth();
            const step = yScale.step();
            const gap = step - height;

            const labelFontSize = clamp(height / 4.5, height * 0.4, 40);

            const labelBottomMargin = Math.max(8, height * 0.088);
            const scoreFontSize = Math.min(height * 0.6, 48);

            const barHeight = height - labelFontSize - labelBottomMargin;

            const scoreAlignment =
              scoreFontSize < barHeight
                ? 'align-to-bar'
                : 'align-to-whole-option';

            const scorePaddingTopIfAlignedToBar =
              labelFontSize + labelBottomMargin;

            const labelValue = labelAccessor(option);

            const position = {
              x: 0,
              y: 0,
              width: x,
              height: barHeight,
              gap,
            };

            const visualization = (
              <HorizontalBarWithPlaceholder
                data-testid={`ranking-bar-${id}`}
                x={position.x}
                y={position.y}
                width={position.width}
                height={position.height}
                background={{ color }}
                placeholderWidth={xScale(domainMax)}
                placeholderColor={placeholderColor}
                bottomLeftRadius={4}
                bottomRightRadius={4}
                topLeftRadius={4}
                topRightRadius={height * 0.2}
                disableAnimations={disableAnimations}
              />
            );

            const visualizationOverride = renderChoiceVisualization?.({
              node: visualization,
              id,
              position,
            });

            return (
              <Box width={barsWidth} height={height} flexDirection="row">
                <Box
                  width={isScoreHidden ? 0 : SCORE_WIDTH}
                  height="100%"
                  mr="16px"
                  justifyContent="center"
                  aria-hidden
                  pt={
                    scoreAlignment === 'align-to-bar'
                      ? scorePaddingTopIfAlignedToBar
                      : 'unset'
                  }
                  style={{
                    opacity: isScoreHidden ? 0 : 1,
                    transition: disableAnimations
                      ? 'none'
                      : 'opacity, width 0.4s ease-in-out',
                    willChange: 'opacity, width',
                  }}
                >
                  <Text
                    fontSize={scoreFontSize}
                    fontWeight="semiBold"
                    width={SCORE_WIDTH}
                    display="inline-flex"
                  >{`${optionIndex + 1}.`}</Text>
                </Box>
                <Box width={barsWidth} height={height}>
                  {visualizationOverride ?? visualization}
                  <OptionLabel
                    id={id}
                    text={labelValue}
                    fontSize={labelFontSize}
                    mb={labelBottomMargin}
                    renderChoiceLabel={renderChoiceLabel}
                    choiceLabelsContainerClassName={
                      choiceLabelsContainerClassName
                    }
                  />
                </Box>
              </Box>
            );
          }}
        />
      </Box>
    </Box>
  );
};
